import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  Trash2,
  ChevronDown,
  ChevronRight,
  List,
  Pencil,
} from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/Card";
import { cn } from "@/lib/cn";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface DropdownCategory {
  id: string;
  category_name: string;
}

interface DropdownItem {
  id: string;
  category_id: string;
  value: string;
  sort_order: number;
  active: number;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function DropdownManager() {
  const queryClient = useQueryClient();
  const [newCategoryName, setNewCategoryName] = useState("");
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const [newItemValue, setNewItemValue] = useState("");
  const [editingItem, setEditingItem] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");

  // Fetch categories
  const { data: categoriesData, isLoading } = useQuery<{ data: DropdownCategory[] }>({
    queryKey: ["dropdown-categories"],
    queryFn: () => api.get("/admin/config/dropdowns"),
  });

  const categories = categoriesData?.data ?? [];

  // Fetch items for expanded category
  const { data: itemsData } = useQuery<{ data: DropdownItem[] }>({
    queryKey: ["dropdown-items", expandedCategory],
    queryFn: () => api.get(`/admin/config/dropdowns/${expandedCategory}/items`),
    enabled: !!expandedCategory,
  });

  const items = itemsData?.data ?? [];

  // Mutations
  const createCategoryMutation = useMutation({
    mutationFn: (categoryName: string) =>
      api.post("/admin/config/dropdowns", { categoryName }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dropdown-categories"] });
      setNewCategoryName("");
    },
  });

  const deleteCategoryMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/admin/config/dropdowns/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dropdown-categories"] });
      if (expandedCategory) setExpandedCategory(null);
    },
  });

  const createItemMutation = useMutation({
    mutationFn: ({ categoryId, value }: { categoryId: string; value: string }) =>
      api.post(`/admin/config/dropdowns/${categoryId}/items`, { value }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dropdown-items", expandedCategory] });
      setNewItemValue("");
    },
  });

  const updateItemMutation = useMutation({
    mutationFn: ({
      categoryId,
      itemId,
      value,
      active,
    }: {
      categoryId: string;
      itemId: string;
      value?: string;
      active?: boolean;
    }) => api.put(`/admin/config/dropdowns/${categoryId}/items/${itemId}`, { value, active }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dropdown-items", expandedCategory] });
      setEditingItem(null);
    },
  });

  const deleteItemMutation = useMutation({
    mutationFn: ({ categoryId, itemId }: { categoryId: string; itemId: string }) =>
      api.delete(`/admin/config/dropdowns/${categoryId}/items/${itemId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dropdown-items", expandedCategory] });
    },
  });

  function handleCreateCategory(e: React.FormEvent) {
    e.preventDefault();
    if (newCategoryName.trim()) {
      createCategoryMutation.mutate(newCategoryName.trim());
    }
  }

  function handleAddItem(e: React.FormEvent) {
    e.preventDefault();
    if (newItemValue.trim() && expandedCategory) {
      createItemMutation.mutate({ categoryId: expandedCategory, value: newItemValue.trim() });
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-foreground">Dropdown Lists</h2>
        <p className="text-sm text-muted-foreground">
          Manage dropdown categories and their values
        </p>
      </div>

      {/* Add category */}
      <Card>
        <CardContent className="p-4">
          <form onSubmit={handleCreateCategory} className="flex items-end gap-3">
            <div className="flex-1">
              <Input
                label="New Category"
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                required
                placeholder="e.g. Referral Sources, Race/Ethnicity"
              />
            </div>
            <Button type="submit" size="sm" className="gap-2" loading={createCategoryMutation.isPending}>
              <Plus className="h-4 w-4" />
              Add Category
            </Button>
          </form>
        </CardContent>
      </Card>

      {isLoading && <div className="h-32 animate-pulse rounded-lg border bg-muted" />}

      {!isLoading && categories.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center">
            <List className="mx-auto h-10 w-10 text-muted-foreground" />
            <p className="mt-2 text-sm text-muted-foreground">
              No dropdown categories configured yet.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Categories with expandable items */}
      {categories.map((cat) => (
        <Card key={cat.id}>
          <CardHeader className="cursor-pointer pb-3" onClick={() =>
            setExpandedCategory(expandedCategory === cat.id ? null : cat.id)
          }>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {expandedCategory === cat.id ? (
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                )}
                <CardTitle className="text-sm font-medium">{cat.category_name}</CardTitle>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={(e) => {
                  e.stopPropagation();
                  if (confirm(`Delete category "${cat.category_name}" and all its items?`)) {
                    deleteCategoryMutation.mutate(cat.id);
                  }
                }}
              >
                <Trash2 className="h-3.5 w-3.5 text-destructive" />
              </Button>
            </div>
          </CardHeader>

          {expandedCategory === cat.id && (
            <CardContent className="pt-0">
              {/* Add item form */}
              <form onSubmit={handleAddItem} className="mb-4 flex items-end gap-3">
                <div className="flex-1">
                  <Input
                    value={newItemValue}
                    onChange={(e) => setNewItemValue(e.target.value)}
                    required
                    placeholder="New value..."
                  />
                </div>
                <Button type="submit" size="sm" loading={createItemMutation.isPending}>
                  Add
                </Button>
              </form>

              {/* Items list */}
              {items.length === 0 ? (
                <p className="text-sm text-muted-foreground">No items in this category.</p>
              ) : (
                <div className="space-y-1">
                  {items.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-center justify-between rounded-md px-3 py-2 hover:bg-muted/50"
                    >
                      {editingItem === item.id ? (
                        <div className="flex flex-1 items-center gap-2">
                          <input
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            className="flex h-8 flex-1 rounded-md border border-input bg-background px-2 text-sm"
                            autoFocus
                          />
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() =>
                              updateItemMutation.mutate({
                                categoryId: cat.id,
                                itemId: item.id,
                                value: editValue,
                              })
                            }
                          >
                            Save
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setEditingItem(null)}
                          >
                            Cancel
                          </Button>
                        </div>
                      ) : (
                        <>
                          <span className={cn("text-sm", !item.active && "text-muted-foreground line-through")}>
                            {item.value}
                          </span>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => {
                                setEditingItem(item.id);
                                setEditValue(item.value);
                              }}
                            >
                              <Pencil className="h-3 w-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() =>
                                updateItemMutation.mutate({
                                  categoryId: cat.id,
                                  itemId: item.id,
                                  active: !item.active,
                                })
                              }
                            >
                              <span className="text-xs">
                                {item.active ? "Disable" : "Enable"}
                              </span>
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => {
                                if (confirm(`Delete "${item.value}"?`)) {
                                  deleteItemMutation.mutate({
                                    categoryId: cat.id,
                                    itemId: item.id,
                                  });
                                }
                              }}
                            >
                              <Trash2 className="h-3 w-3 text-destructive" />
                            </Button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          )}
        </Card>
      ))}
    </div>
  );
}
